import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getFullName } from "src/utils/utils";

import Thumbnail from "src/components/thumbnail";
import Button from "src/components/button";
import { LearningRecordSimulation } from "src/types/coaching/learners";

import { isSimulationVideoFormat } from "src/constants/simulation";

import { SimulationContext } from "../context";

import _ from "lodash";
import cx from "classnames";
import styles from "./deviceSetup.module.scss";

export interface DeviceSetupViewProps {
  simulation: LearningRecordSimulation;
  onConfirmStart: () => void;
  onClose: () => void;
  isSubmitting?: boolean;
  className?: string;
}

const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const logAudioStreamDebug = (source: string, stream: MediaStream) => {
  const audioTrack = stream.getAudioTracks()[0];
  console.log(`[DeviceSetup] ${source} stream`, stream);
  console.log(`[DeviceSetup] ${source} audio tracks`, stream.getAudioTracks());
  if (audioTrack) {
    console.log(`[DeviceSetup] ${source} readyState`, audioTrack.readyState);
    console.log(`[DeviceSetup] ${source} enabled`, audioTrack.enabled);
    console.log(`[DeviceSetup] ${source} muted`, audioTrack.muted);
  }
};

const probeAudioStream = async (source: string) => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    logAudioStreamDebug(source, stream);
    stream.getTracks().forEach((track) => track.stop());
  } catch (error) {
    console.error(`[DeviceSetup] ${source} getUserMedia failed`, error);
  }
};

export const DeviceSetup: React.FC<DeviceSetupViewProps> = ({
  simulation,
  onConfirmStart,
  onClose,
  isSubmitting,
  className,
}) => {
  const { devices, selectedDevices, setDevices, onDeviceChange } = React.useContext(SimulationContext);

  const mountedRef = React.useRef(true);
  const audioPermissionStatusRef = React.useRef<PermissionStatus | null>(null);
  const videoPermissionStatusRef = React.useRef<PermissionStatus | null>(null);
  const selectedDevicesRef = React.useRef(selectedDevices);
  selectedDevicesRef.current = selectedDevices;

  const isVideo = isSimulationVideoFormat(simulation.simulation?.format);

  const [permissionError, setPermissionError] = useState({
    audio: false,
    video: false,
    audioDenied: false,
    videoDenied: false,
  });

  const { name, icon } = useSelector(
    (state: any) => {
      const userProfile = _.get(state, "users.me");
      return { name: getFullName(userProfile), icon: _.get(userProfile, "icon") };
    },
    _.isEqual,
  );

  const safeSetPermissionError = useCallback((updater: React.SetStateAction<typeof permissionError>) => {
    if (mountedRef.current) setPermissionError(updater);
  }, []);

  const enumerateDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      console.log("All Devices", allDevices);
      if (!mountedRef.current) return;

      const grouped = allDevices.reduce(
        (acc, device) => {
          acc[device.kind].push(device);
          return acc;
        },
        {
          audioinput: [] as MediaDeviceInfo[],
          audiooutput: [] as MediaDeviceInfo[],
          videoinput: [] as MediaDeviceInfo[],
        },
      );

      setDevices?.({
        audioInputs: grouped.audioinput,
        audioOutputs: grouped.audiooutput,
        videoInputs: grouped.videoinput,
      });

      const prev = selectedDevicesRef.current ?? { audioInput: "", audioOutput: "", videoInput: "" };
      if (!prev.audioInput) {
        const defaultDevice = grouped.audioinput.find(device => device.deviceId === "default");
        const id = grouped.audioinput.find(device =>
          device.deviceId !== "default" &&
          device.deviceId !== "communications" &&
          device.groupId === defaultDevice?.groupId
        )?.deviceId ?? grouped.audioinput.find(device =>
          device.deviceId !== "default" && device.deviceId !== "communications"
        )?.deviceId ?? "";
        if (id) onDeviceChange?.("audioInput", id);
      }
      if (!prev.audioOutput) {
        const defaultDevice = grouped.audiooutput.find(device => device.deviceId === "default");
        const id = grouped.audiooutput.find(device =>
          device.deviceId !== "default" &&
          device.deviceId !== "communications" &&
          device.groupId === defaultDevice?.groupId
        )?.deviceId ?? grouped.audiooutput.find(device =>
          device.deviceId !== "default" && device.deviceId !== "communications"
        )?.deviceId ?? "";
        if (id) onDeviceChange?.("audioOutput", id);
      }
      if (isVideo && !prev.videoInput) {
        const id = grouped.videoinput.find(device => device.deviceId !== "default")?.deviceId ?? "";
        if (id) onDeviceChange?.("videoInput", id);
      }
    } catch (error) {
      console.error("Error enumerating devices", error);
    }
  }, [isVideo, setDevices, onDeviceChange]);

  const attachPermissionListener = useCallback((
    status: PermissionStatus,
    ref: React.MutableRefObject<PermissionStatus | null>,
    onChange: () => void,
  ) => {
    if (ref.current) ref.current.onchange = null;
    ref.current = status;
    try {
      status.addEventListener("change", onChange);
    } catch {
      status.onchange = onChange;
    }
  }, []);

  const checkPermissionsStandard = useCallback(async () => {
    try {
      const audioState = await navigator.permissions.query({ name: "microphone" as PermissionName });
      if (!mountedRef.current) return;

      if (audioState.state === "granted") {
        safeSetPermissionError((prev) => ({ ...prev, audio: false, audioDenied: false }));
        await enumerateDevices();
      } else {
        safeSetPermissionError((prev) => ({
          ...prev,
          audio: true,
          audioDenied: audioState.state === "denied",
        }));
      }

      attachPermissionListener(audioState, audioPermissionStatusRef, () => {
        if (mountedRef.current) void checkPermissionsStandard();
      });
    } catch {
      // Firefox: permissions.query unsupported — fall back to prompt flow
      safeSetPermissionError((prev) => ({ ...prev, audio: true, audioDenied: false }));
    }

    if (!isVideo) return;

    try {
      const videoState = await navigator.permissions.query({ name: "camera" as PermissionName });
      if (!mountedRef.current) return;

      if (videoState.state === "granted") {
        safeSetPermissionError((prev) => ({ ...prev, video: false, videoDenied: false }));
      } else {
        safeSetPermissionError((prev) => ({
          ...prev,
          video: true,
          videoDenied: videoState.state === "denied",
        }));
      }

      attachPermissionListener(videoState, videoPermissionStatusRef, () => {
        if (mountedRef.current) void checkPermissionsStandard();
      });
    } catch {
      safeSetPermissionError((prev) => ({ ...prev, video: true, videoDenied: false }));
    }
  }, [isVideo, enumerateDevices, safeSetPermissionError, attachPermissionListener]);

  const checkPermissions = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermissionError({ audio: true, video: true, audioDenied: false, videoDenied: false });
      return;
    }
    if (isSafari()) {
      try {
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        if (!mountedRef.current) return;
        const audioGranted = allDevices.some(d => d.kind === "audioinput" && d.label);
        const videoGranted = allDevices.some(d => d.kind === "videoinput" && d.label);
        if (audioGranted) {
          safeSetPermissionError(prev => ({ ...prev, audio: false, audioDenied: false }));
          await enumerateDevices();
        } else {
          safeSetPermissionError(prev => ({ ...prev, audio: true, audioDenied: false }));
        }
        if (isVideo) {
          safeSetPermissionError(prev => ({ ...prev, video: !videoGranted, videoDenied: false }));
        }
      } catch {
        safeSetPermissionError(prev => ({
          ...prev,
          audio: true,
          audioDenied: false,
          ...(isVideo ? { video: true, videoDenied: false } : {}),
        }));
      }
    } else {
      await checkPermissionsStandard();
    }
  }, [isVideo, checkPermissionsStandard, safeSetPermissionError, enumerateDevices]);

  const handleRequestAccess = async (type: "audio" | "video") => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        type === "audio" ? { audio: true } : { video: true },
      );
      if (type === "audio") {
        logAudioStreamDebug("allow", stream);
      }
      stream.getTracks().forEach((track) => track.stop());
      if (!mountedRef.current) return;
      safeSetPermissionError((prev) => ({
        ...prev,
        [type]: false,
        [type === "audio" ? "audioDenied" : "videoDenied"]: false,
      }));
      await enumerateDevices();
    } catch (error: any) {
      let isDenied = false;
      if (isSafari()) {
        isDenied = error?.name === "NotAllowedError";
      } else {
        try {
          const permName = type === "audio" ? "microphone" : "camera";
          const { state } = await navigator.permissions.query({ name: permName as PermissionName });
          isDenied = state === "denied";
        } catch {
          isDenied = true;
        }
      }
      safeSetPermissionError((prev) => ({
        ...prev,
        [type]: true,
        [type === "audio" ? "audioDenied" : "videoDenied"]: isDenied,
      }));
    }
  };

  const primaryLabel = useMemo(() => {
    const { status, report } = simulation;
    if (status === "COMPLETED" && report?.status === "in_sufficient_duration") return "Restart call";
    if (status === "COMPLETED") return "Retake call";
    return "Start call";
  }, [simulation.status, simulation.report]);

  const permissionsBlockStart = !!permissionError.audio || (isVideo && !!permissionError.video);

  const handleStart = () => {
    if (!permissionsBlockStart && !isSubmitting) onConfirmStart();
  };

  useEffect(() => {
    probeAudioStream("start-call");
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void checkPermissions();
  
    const onDeviceChangeGlobal = async () => {
      if (!mountedRef.current) return;
  
      if (simulation.status === "IN_PROGRESS") {
        await enumerateDevices();
        return;
      }
  
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      if (!mountedRef.current) return;
  
      const audioInputs = allDevices.filter(device => device.kind === "audioinput" && device.deviceId !== "default" && device.deviceId !== "communications");
      const audioOutputs = allDevices.filter(device => device.kind === "audiooutput" && device.deviceId !== "default" && device.deviceId !== "communications");
      const videoInputs = allDevices.filter(device => device.kind === "videoinput" && device.deviceId !== "default");
  
      const prev = selectedDevicesRef.current;
  
      if (!audioInputs.some(device => device.deviceId === prev?.audioInput) && audioInputs.length) {
        const systemDefault = allDevices.find(device => device.kind === "audioinput" && device.deviceId === "default");
        const fallback = audioInputs.find(device => device.groupId === systemDefault?.groupId) ?? audioInputs[0];
        onDeviceChange?.("audioInput", fallback.deviceId);
      }
      if (!audioOutputs.some(device => device.deviceId === prev?.audioOutput) && audioOutputs.length) {
        const systemDefault = allDevices.find(device => device.kind === "audiooutput" && device.deviceId === "default");
        const fallback = audioOutputs.find(device => device.groupId === systemDefault?.groupId) ?? audioOutputs[0];
        onDeviceChange?.("audioOutput", fallback.deviceId);
      }
      if (isVideo && !videoInputs.some(device => device.deviceId === prev?.videoInput) && videoInputs.length) {
        onDeviceChange?.("videoInput", videoInputs[0].deviceId);
      }
  
      await checkPermissions();
    };
  
    navigator.mediaDevices.addEventListener("devicechange", onDeviceChangeGlobal);
  
    return () => {
      mountedRef.current = false;
      navigator.mediaDevices.removeEventListener("devicechange", onDeviceChangeGlobal);
      if (audioPermissionStatusRef.current) {
        audioPermissionStatusRef.current.onchange = null;
        audioPermissionStatusRef.current = null;
      }
      if (videoPermissionStatusRef.current) {
        videoPermissionStatusRef.current.onchange = null;
        videoPermissionStatusRef.current = null;
      }
    };
  }, [isVideo, checkPermissions, enumerateDevices]);

  const renderAvatar = () => {
    if (permissionError.audio || (isVideo && permissionError.video)) {
      return (
        <div
          className={cx(
            styles.videoPreparingOverlay,
            styles.errorContainer,
            isVideo ? styles.videoLoading : styles.audioLoading,
          )}
        >
          <div className={styles.permissionContainer}>
            {permissionError.audio && !permissionError.audioDenied && (
              <div className={styles.errorMessage}>
                <p className={styles.heading}>Microphone access required</p>
                <p className={styles.message}>
                Allow microphone access to get started.
                </p>
                <Button
                  className={styles.button}
                  text="Allow"
                  onClick={() => handleRequestAccess("audio")}
                  icon="paperflite-right-arrow"
                  variant="courses-primary"
                />
              </div>
            )}
            {permissionError.audio && permissionError.audioDenied && (
              <div className={styles.errorMessage}>
                <p className={styles.heading}>Microphone access blocked</p>
                <p className={styles.message}>
                Check your browser permissions and allow microphone access to get started.
                </p>
              </div>
            )}
            {!permissionError.audio && isVideo && permissionError.video && !permissionError.videoDenied && (
              <div className={styles.errorMessage}>
                <p className={styles.heading}>Camera access required</p>
                <p className={styles.message}>
                Allow camera access to get started.
                </p>
                <Button
                  className={styles.button}
                  text="Allow"
                  onClick={() => handleRequestAccess("video")}
                  icon="paperflite-right-arrow"
                  variant="courses-primary"
                />
              </div>
            )}
            {!permissionError.audio && isVideo && permissionError.video && permissionError.videoDenied && (
              <div className={styles.errorMessage}>
                <p className={styles.heading}>Camera access blocked</p>
                <p className={styles.message}>
                Check your browser permissions and allow camera access to get started.
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <Thumbnail
        className={cx(styles.icon, isVideo ? styles.videoLoading : styles.audioLoading)}
        media={icon}
        defaultImage="/images/assets/default_asset.jpg"
      />
    );
  };

  const deviceSetupDisabled =
    !devices?.audioInputs?.length ||
    permissionError.audio ||
    (isVideo && permissionError.video);

  return (
    <div className={cx(styles.callSimulationContainer, className)}>
      <div className={styles.mainContainer}>
        <div className={styles.header}>
          <img className={styles.logo} src="/images/coaching/logo.svg" alt="Coaching" />
          <div className={styles.divider} />
          <div className={styles.name}>{_.get(simulation, "simulation.name") || ""}</div>
          <div className={styles.actions}>
            <button type="button" className={styles.action} onClick={onClose}>
              <i className="paperflite-close" />
            </button>
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.callContainer}>
            <div className={styles.participants}>
              <div className={cx(styles.participantContainer, styles.user)}>
                <div className={styles.participant}>
                  <div className={styles.avatar}>
                    {renderAvatar()}
                    <div className={styles.voiceVisualizer}>
                      <span className={styles.voiceName}>{name}</span>
                    </div>
                  </div>
                </div>

                <div className={cx(styles.deviceSetup, { [styles.disabled]: deviceSetupDisabled })}>
                  <SimulationDeviceDropdown
                    icon="paperflite-microphone"
                    value={devices?.audioInputs?.find((d) => d.deviceId === selectedDevices?.audioInput)}
                    options={devices?.audioInputs?.filter((d) => d.deviceId !== "default") || []}
                    onChange={(id: string) => onDeviceChange?.("audioInput", id)}
                    placeholder="Microphone"
                  />
                  {!!devices?.audioOutputs?.length && (
                    <SimulationDeviceDropdown
                      icon="paperflite-volume-on"
                      value={devices.audioOutputs.find((d) => d.deviceId === selectedDevices?.audioOutput)}
                      options={devices.audioOutputs.filter((d) => d.deviceId !== "default")}
                      onChange={(id: string) => {
                        onDeviceChange?.("audioOutput", id);
                        const selectedOutput = devices.audioOutputs.find(device => device.deviceId === id);
                        const pairedMic = devices?.audioInputs?.find(
                          device => device.groupId === selectedOutput?.groupId && device.deviceId !== "default"
                        );
                        if (pairedMic) onDeviceChange?.("audioInput", pairedMic.deviceId);
                      }}
                      placeholder="Speaker"
                    />
                  )}
                  {isVideo && (
                    <SimulationDeviceDropdown
                      icon="paperflite-video"
                      value={devices?.videoInputs?.find((d) => d.deviceId === selectedDevices?.videoInput)}
                      options={devices?.videoInputs?.filter((d) => d.deviceId !== "default") || []}
                      onChange={(id: string) => onDeviceChange?.("videoInput", id)}
                      placeholder="Camera"
                      className={styles.videoIcon}
                    />
                  )}
                  <Button
                    className={styles.startCallButton}
                    text={isSubmitting ? "Please wait..." : primaryLabel}
                    onClick={handleStart}
                    variant="courses-primary"
                    disabled={permissionsBlockStart || !!isSubmitting}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SimulationDeviceDropdown = ({
  icon,
  mutedIcon,
  isMuted,
  onMuteToggle,
  value,
  options,
  onChange,
  placeholder,
  className,
  simulator = false,
}: any) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  return (
    <div className={cx(styles.dropdown, { [styles.simulator]: simulator })}>
      <div className={styles.dropdownControl} onClick={(e) => {
        e.stopPropagation();
        setOpen((prev) => !prev);
      }}>
        <i
          className={cx(styles.iconWrapper, className,  {
            [styles.muted]: isMuted,
            [styles.microphoneIcon]: placeholder === "Microphone" || placeholder === "Camera",
          })}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <i className={cx(isMuted && mutedIcon ? mutedIcon : icon, styles.dropdownIcon)} />
        </i>

        <span
          className={styles.dropdownText}>
          {value?.label || placeholder}
        </span>
        <i
          className={styles.chevron}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
        >
          <i
            className={cx("paperflite-dropdown", {
              [styles.open]: open,
            })}
          />
        </i>
      </div>

      {open && (
        <div className={styles.dropdownMenu} onClick={(e) => e.stopPropagation()}>
          {options.map((opt: any) => (
            <div
              key={opt.deviceId}
              className={cx(styles.dropdownItem, {
                [styles.selected]: value?.deviceId === opt.deviceId,
              })}
              onClick={() => {
                onChange(opt.deviceId);
                setOpen(false);
              }}
            >
              {opt.label || placeholder}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};